import React, { useState, useEffect } from "react";
import { partnersAPI } from "../../utils/api";
import { formatApiError } from "../../utils/errorFormatter";
import { X, Loader2, Plus, Trash2, User } from "lucide-react";
import { toast } from "sonner";

const PartnerModal = ({ isOpen, onClose, partner = null, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [formData, setFormData] = useState({
    partner_name: "",
    nature_of_business: "",
    products_services: "",
    partnership_type: "Referral",
    address: "",
    website: "",
    notes: "",
    active: true
  });

  const [contacts, setContacts] = useState([
    { name: "", designation: "", email: "", phone: "", is_primary: true }
  ]);

  useEffect(() => {
    if (isOpen) {
      if (partner) {
        setFormData({
          partner_name: partner.partner_name || "",
          nature_of_business: partner.nature_of_business || "",
          products_services: partner.products_services || "",
          partnership_type: partner.partnership_type || "Referral",
          address: partner.address || "",
          website: partner.website || "",
          notes: partner.notes || "",
          active: partner.active !== false
        });
        
        if (partner.contacts && partner.contacts.length > 0) {
          setContacts(partner.contacts);
        } else {
          setContacts([{ name: "", designation: "", email: "", phone: "", is_primary: true }]);
        }
      } else {
        // Reset for new partner
        setFormData({
          partner_name: "",
          nature_of_business: "",
          products_services: "",
          partnership_type: "Referral",
          address: "",
          website: "",
          notes: "",
          active: true
        });
        setContacts([{ name: "", designation: "", email: "", phone: "", is_primary: true }]);
      }
      setError(null);
    }
  }, [isOpen, partner]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const addContact = () => {
    setContacts([...contacts, { name: "", designation: "", email: "", phone: "", is_primary: false }]);
  };

  const removeContact = (index) => {
    if (contacts.length > 1) {
      const newContacts = contacts.filter((_, i) => i !== index);
      // If removed the primary, make first one primary
      if (contacts[index].is_primary && newContacts.length > 0) {
        newContacts[0].is_primary = true;
      }
      setContacts(newContacts);
    }
  };

  const updateContact = (index, field, value) => {
    const updated = [...contacts];
    if (field === 'is_primary' && value) {
      // Only one can be primary
      updated.forEach((c, i) => {
        c.is_primary = i === index;
      });
    } else {
      updated[index] = { ...updated[index], [field]: value };
    }
    setContacts(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (!formData.partner_name.trim()) {
        throw new Error("Partner name is required");
      }

      // Filter out empty contacts
      const validContacts = contacts.filter(c => c.name.trim());

      const payload = {
        ...formData,
        contacts: validContacts
      };

      if (partner) {
        await partnersAPI.update(partner._id, payload);
        toast.success("Partner updated");
      } else {
        await partnersAPI.create(payload);
        toast.success("Partner created");
      }

      onSuccess?.();
      onClose();
    } catch (err) {
      setError(formatApiError(err, "Failed to save partner"));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!partner || !window.confirm("Are you sure you want to delete this partner?")) return;
    
    try {
      await partnersAPI.delete(partner._id);
      toast.success("Partner deleted");
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error("Failed to delete partner");
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="partner-modal-overlay"
    >
      <div 
        className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="partner-modal"
      >
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-semibold text-slate-900">
            {partner ? "Edit Partner" : "Create New Partner"}
          </h2>
          <button 
            type="button"
            onClick={onClose} 
            className="text-slate-400 hover:text-slate-600 p-1 rounded-full hover:bg-slate-100 transition-colors"
            data-testid="partner-modal-close"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Company Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">Partner/Company Name *</label>
                <input
                  type="text"
                  name="partner_name"
                  value={formData.partner_name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                  data-testid="partner-name-input"
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">Nature of Business</label>
                <input
                  type="text"
                  name="nature_of_business"
                  value={formData.nature_of_business}
                  onChange={handleChange}
                  placeholder="e.g., IT Solutions Provider, Consulting Firm"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="nature-of-business-input"
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">Products/Services</label>
                <textarea
                  name="products_services"
                  value={formData.products_services}
                  onChange={handleChange}
                  rows="2"
                  placeholder="Describe the products or services offered"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="products-services-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Partnership Type</label>
                <select
                  name="partnership_type"
                  value={formData.partnership_type}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="partnership-type-select"
                >
                  <option value="Referral">Referral Partner</option>
                  <option value="Reseller">Reseller</option>
                  <option value="Strategic">Strategic Partner</option>
                  <option value="Technology">Technology Partner</option>
                  <option value="Implementation">Implementation Partner</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Website</label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  placeholder="https://www.example.com"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">Address</label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  rows="2"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div className="col-span-2 flex items-center gap-2">
                <input
                  type="checkbox"
                  name="active"
                  id="active"
                  checked={formData.active}
                  onChange={handleChange}
                  className="w-4 h-4 text-primary rounded border-slate-300 focus:ring-primary"
                />
                <label htmlFor="active" className="text-sm text-slate-700">Active Partner</label>
              </div>
            </div>
          </div>

          {/* Contact Persons (PICs) */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">Contact Persons (PICs)</h3>
              <button
                type="button"
                onClick={addContact}
                className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors border border-green-200"
                data-testid="add-contact-btn"
              >
                <Plus size={16} />
                Add Contact
              </button>
            </div>

            <div className="space-y-4">
              {contacts.map((contact, index) => (
                <div 
                  key={index} 
                  className="p-4 bg-slate-50 rounded-lg border border-slate-200"
                  data-testid={`contact-row-${index}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <User size={16} className="text-slate-500" />
                      <span className="text-sm font-medium text-slate-700">Contact {index + 1}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="flex items-center gap-1 text-xs text-slate-600">
                        <input
                          type="radio"
                          name="primary_contact"
                          checked={contact.is_primary}
                          onChange={() => updateContact(index, 'is_primary', true)}
                          className="text-primary focus:ring-primary"
                        />
                        Primary
                      </label>
                      {contacts.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeContact(index)}
                          className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors"
                          data-testid={`remove-contact-${index}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-slate-600 mb-1">Name</label>
                      <input
                        type="text"
                        value={contact.name}
                        onChange={(e) => updateContact(index, 'name', e.target.value)}
                        placeholder="Full name"
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                        data-testid={`contact-name-${index}`}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-600 mb-1">Designation</label>
                      <input
                        type="text"
                        value={contact.designation}
                        onChange={(e) => updateContact(index, 'designation', e.target.value)}
                        placeholder="Job title"
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                        data-testid={`contact-designation-${index}`}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-600 mb-1">Email</label>
                      <input
                        type="email"
                        value={contact.email}
                        onChange={(e) => updateContact(index, 'email', e.target.value)}
                        placeholder="email@example.com"
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                        data-testid={`contact-email-${index}`}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-600 mb-1">Phone</label>
                      <input
                        type="tel"
                        value={contact.phone}
                        onChange={(e) => updateContact(index, 'phone', e.target.value)}
                        placeholder="+60 12-345 6789"
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                        data-testid={`contact-phone-${index}`}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="3"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 justify-between pt-4 border-t border-slate-200">
            <div>
              {partner && (
                <button
                  type="button"
                  onClick={handleDelete}
                  className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  data-testid="delete-partner-btn"
                >
                  Delete Partner
                </button>
              )}
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                data-testid="partner-submit-btn"
              >
                {loading && <Loader2 className="animate-spin" size={16} />}
                {partner ? "Update Partner" : "Create Partner"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PartnerModal;
